
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import Toast from '../../helpers/Toast';
import { GetFileMetadataDTO } from '../../models/File/GetFileMetadataDTO';
import { GetPaginatedFileListDTO } from '../../models/File/GetPaginatedFileListDTO';
import { AssistanceService } from '../../services/AssistanceService';
import { FileTypeService } from '../../services/FileTypeService';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import ModalComponent from '../ModalComponent';
import { SelectItemsProps } from '../Select2Component';
import SelectComponent from '../SelectComponent';
import TableComponent from '../TableComponent';
import ChooseFileButton from './ChooseFileButton';
import ImagePopup from './ImagePopUp';
import JSZip from "jszip"
import saveAs from 'file-saver';
import useValidationSchema, { FieldConfig } from '../Validations';

export type FilesListProps = {
    assistanceId: number;
    reloadExecutions: boolean;
    mode: "workshop" | "backoffice";
}

export function FilesList({ assistanceId, reloadExecutions, mode }: FilesListProps) {

    const intl = useIntl();
    const zip = new JSZip();
    const [files, setFiles] = useState<GetFileMetadataDTO[]>([]);
    const assistanceService = new AssistanceService();
    const fileTypeService = new FileTypeService();
    const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
    const [fullScreenFileType, setFullScreenFileType] = useState<string | null>(null);
    const pageSizes: number[] = [5, 15, 30];
    const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
    const [isViewLoading, setIsViewLoading] = useState<boolean>(false);
    const [file, setFile] = useState<File | null>(null);
    const [resetFile, setResetFile] = useState<number>(0);
    const [goToPage, setGoToPage] = useState<number>(1);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [removeFile, setRemovedFile] = useState<GetFileMetadataDTO>();
    const [notes, setNotes] = useState<string>("");
    const [reload, setReload] = useState<boolean>(false);

    const [fileTypeSelected, setFiletypeSelected] = useState<string>("")
    const [listTypeFiles, setListTypeFiles] = useState<SelectItemsProps[]>([]);

    const [isDownloadDisabled, setIsDownloadDisabled] = useState<boolean>(true);



    const viewFile = async (fileId: number) => {
        try {
            setIsViewLoading(true);
            const blob = await assistanceService.StreamFile(fileId);
            const blobUrl = URL.createObjectURL(blob);

            if (blob.type.startsWith("image") || blob.type.startsWith("video")) {
                setFullScreenFileType(blob.type);
                setFullScreenImage(blobUrl);
            } else {
                let newWindow = window.open("");
                newWindow!.document.write(`<iframe width='100%' height='100%' src='${blobUrl}'></iframe>`);
            }
        } catch (error) {
            Toast.Show("error", "Ocorreu um erro ao visualizar o ficheiro.");
        } finally {
            setIsViewLoading(false);
        }
    }
    const onFileChange = (file: File | null) => {
        setFile(file);
    }

    const fields: FieldConfig[] = [
        ['fileTypeSelected', 'string', 'Screens.Assistances.Files.Insert.Validation.FileTypeRequired'],
        ['notes', 'string', 'Screens.Assistances.Files.Insert.Validation.NotesRequired']
    ];

    const schemaClientValidation = useValidationSchema(fields);

    const insertFile = async () => {
        if (typeof file === "undefined" || file == null) {
            Toast.Show("error", intl.formatMessage({ id: "Common.Files.FilesPopup.FileMustBeSelected" }));
            return;
        }

        const insertedFile = {
            fileTypeSelected: fileTypeSelected,
            notes: notes
        }

        const { error } = schemaClientValidation.validate(insertedFile);

        if (error) {
            Toast.Show("error", error.details[0].message);
            setIsButtonLoading(false);
            return;
        }

        setIsLoadingTable(true);
        setIsButtonLoading(true);

        var response = await assistanceService.InsertFile(file, assistanceId, fileTypeSelected, notes);

        if (response.success === false) {
            setIsButtonLoading(false);
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return;
        }

        setFile(null);
        setResetFile(resetFile + 1);
        setIsLoadingTable(false);
        setIsButtonLoading(false);
        Toast.Show("success", intl.formatMessage({ id: "Common.Files.FilesPopup.InsertSuccess" }));
        setReload(!reload);

    }

    const fetchData = async (currentPage: number, pageSize: number) => {
        setIsLoadingTable(true);

        var data: GetPaginatedFileListDTO = {
            currentPage: currentPage,
            pageSize: pageSize,
            assistanceId: assistanceId,
        }

        var response = await assistanceService.GetFilesMetadata(data);

        if (response.count <= 0) {
            setIsDownloadDisabled(true);

        } else {
            setIsDownloadDisabled(false);
        }

        if (response.success === false) {
            setFiles([]);
            setGoToPage(0);
            setIsLoadingTable(false);
            setIsDownloadDisabled(true);
            Toast.Show("error", response.message);
            return;
        }

        setFiles(response.items);
        setIsLoadingTable(false);
        setTotalPages(response.totalPages);
        setPageSize(pageSize);
        setCurrentPage(currentPage);
        setGoToPage(0)

        if (response.totalPages > 0 && currentPage > response.totalPages) {
            setGoToPage(1);
        }
    };

    const getTypeFile = async () => {
        var response = await fileTypeService.GetAll();
        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        var items: SelectItemsProps[] = [];
        response.obj.forEach((element) => {
            var item: SelectItemsProps = {
                value: element.id.toString(),
                label: element.name
            }
            items.push(item);
        });

        setListTypeFiles(items);
    }

    const downloadFiles = async () => {
        try {
            setIsButtonLoading(true);

            for (const file of files) {
                const blob = await assistanceService.StreamFile(file.fileId);
                zip.file(file.originalName, blob);
            }

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "files.zip");

            Toast.Show("success", "Os ficheiros foram transferidos com sucesso!");
        } catch (error) {
            Toast.Show("error", "Ocorreu um erro ao transferir os ficheiros.");
        } finally {
            setIsButtonLoading(false);
        }
    }

    const DownloadSingleFile = async (fileId: number, originalName: string) => {
        try {
            setIsButtonLoading(true);

            const blob = await assistanceService.StreamFile(fileId);

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = originalName;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);

            Toast.Show("success", "O ficheiro foi transferido com sucesso!");
        } catch (error) {
            Toast.Show("error", "Ocorreu um erro ao transferir o ficheiro.");
        } finally {
            setIsButtonLoading(false);
        }
    }













    const Remove = async (idFile: number) => {
        setIsLoadingTable(true);
        var response = await assistanceService.RemoveFile(assistanceId, idFile);
        if (response.success === false) {
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return
        }
        setIsLoadingTable(false);
        Toast.Show("success", intl.formatMessage({ id: "Common.Files.FilesPopup.RemoveSuccess" }));
        setReload(!reload);

    }



    useEffect(() => {
        getTypeFile();
    }, [])

    useEffect(() => {
        fetchData(currentPage, pageSize)
    }, [reload])

    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.Assistances.Files.Type" />,
                accessor: "fileType",
                width: 30,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Files.Desc" />,
                accessor: "notes",
                width: 35,
            },
            {
                Header: "Opções",
                Cell: (props: any) => {
                    return (
                        <>
                            <div style={{ display: "flex", justifyContent: "space-around" }}>

                                <div style={{ display: "inline-flex", height: "30px" }}>
                                    <ButtonComponent
                                        text={intl.formatMessage({ id: "FilesList.See" })}
                                        disabled={isLoadingTable || isViewLoading || isButtonLoading}
                                        loading={isViewLoading}
                                        onClick={() => {
                                            viewFile(props.row.original.fileId);
                                        }} />
                                </div>
                                <div style={{ display: "inline-flex", height: "30px" }}>
                                    <ButtonComponent
                                        text={intl.formatMessage({ id: "FilesList.Remove" })}
                                        disabled={isLoadingTable || isViewLoading || isButtonLoading}
                                        onClick={() => {
                                            setIsOpen(true);
                                            setRemovedFile(props.cell.row.original)
                                        }} />
                                </div>

                                <div style={{ display: "inline-flex", height: "30px" }}>
                                    <ButtonComponent
                                        text={intl.formatMessage({ id: "FilesList.Download" })}
                                        onClick={() => DownloadSingleFile(props.row.original.fileId, props.row.original.originalName)}
                                        loading={isButtonLoading}
                                        disabled={isViewLoading || isButtonLoading}
                                    />
                                </div>



                            </div>
                        </>
                    );
                },
                width: 35,
            }
        ],
        [isViewLoading, isButtonLoading, isLoadingTable],
    );

    return (
        <>
            <Row style={{ margin: "1rem 0px" }} >
                <ChooseFileButton reset={resetFile} onFileChange={onFileChange} />
                <SelectComponent xl={3} lg={3} md={3} sm={6} xs={12}
                    style={{ width: "100%" }}
                    label={"Tipo de Ficheiro"}
                    selectedItemValue={fileTypeSelected}
                    showLoadingSkeleton={isButtonLoading}
                    items={listTypeFiles}
                    onChange={(t: string) => setFiletypeSelected(t)}
                />

                <InputComponent xl={3} lg={3} md={3} sm={6} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={notes}
                    label={"Descrição"}
                    readOnly={false}
                    showLoadingSkeleton={isButtonLoading}
                    onChange={(t: string) => setNotes(t)}
                />
                <Col xl={3} lg={3} sm={3} xs={12}>
                    <ButtonComponent
                        style={{ width: '100%', marginTop: '30px' }}
                        onClick={() => insertFile()}
                        text={intl.formatMessage({ id: "FilesList.Insert" })}
                        loading={isButtonLoading}
                    />
                </Col>


                <h5 style={{ marginTop: "10px" }}><FormattedMessage id={"Screens.Assistances.Files.Table.Title"}></FormattedMessage></h5>
                <ModalComponent IsOpen={isOpen} text={`Tem a certeza que quer remover o ficheiro '${removeFile?.originalName}' da assistência com o id ${assistanceId}`} OnClickRight={() => setIsOpen(false)} OnClickLeft={() => { setIsOpen(false); Remove(removeFile?.fileId ?? 0) }} title={"Remover ficheiro da assistência"} buttonRight={"Não"} buttonLeft={"Sim"} />
                <TableComponent
                    columns={columns}
                    data={files}
                    goToPage={goToPage}
                    initialPage={currentPage}
                    pageCount={totalPages}
                    initialPageSize={pageSize}
                    pageSizes={pageSizes}
                    manualPagination={true}
                    loading={isLoadingTable}
                    fetchData={fetchData}
                    initialSortBy={[]}
                    disableSortBy={true}
                />
            </Row>
            <Row style={{ display: "flex", justifyContent: "start", alignItems: "center", margin: "auto" }}>
                <Col xl={4} lg={4} md={4} sm={4} xs={12}>
                    <ButtonComponent
                        style={{ width: '100%', marginBottom: "20px" }}
                        onClick={() => downloadFiles()}
                        text={intl.formatMessage({ id: "FilesList.Download" })}
                        loading={isButtonLoading}
                        disabled={isDownloadDisabled}
                    />
                </Col>
            </Row>

            <ImagePopup path={fullScreenImage} fileType={fullScreenFileType} onClose={() => { setFullScreenImage(null); setFullScreenFileType(null); }} />
        </>
    )
}
